/**
 * Hooks for Section Review Queue (Phase 18.5.4)
 *
 * Manages state and API calls for the manual section assignment
 * review system when section matching returns 0 matches.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

interface SectionReviewTrigger {
  id: string;
  triggerType: string;
  sopId: string | null;
  sopTitle: string | null;
  cameraId: string | null;
  operatorId: string | null;
  operatorName: string | null;
  startTime: string;
  endTime: string;
  // The AI's free text: sent to admin+ only (Phase 41A spec D13), so absent
  // for a trainer.
  observationSummary?: string | null;
  aiReasoningChain?: string | null;
  // Phase 36.1d. NULL means `no_match` — every trigger that predates 36.1d, and
  // every one where Stage 5b found nothing. `supervised` means the AI DID pick
  // `detectedSectionId` and the trainer is being asked to judge that pick.
  reviewReason: 'no_match' | 'supervised' | null;
  detectedSectionId: string | null;
  // The REAL model confidence, never 1.0 on a held trigger — 1.0 is the
  // sentinel for "a human assigned this". Null on the PATH C legacy path, in
  // which case the card omits the percentage rather than rendering NaN%.
  detectedSectionConfidence: number | null;
  triggeredAt: string;
  videoClipId: string | null;
  videoUrl: string | null;
  // `inScope` (Phase 36.4): the template's declared Job scope named this Job.
  // Always true when the trigger carries no scope, which is every pre-36.4
  // trigger. Sorted scoped-first by the API.
  sections: Array<{ id: string; title: string; sectionNumber: number; description: string | null; inScope: boolean }>;
  // Phase 36.1c-1: what a trainer has confirmed before on this exact
  // (SOP, camera, lens). `sectionId` resolves against `sections` above.
  //
  // ⚠️ `totalCount` is the corpus size and drives the panel's presentation.
  // `sections.length` is NOT it — that array is capped at 5 Jobs server-side.
  priors: {
    totalCount: number;
    sections: Array<{
      sectionId: string;
      count: number;
      lastConfirmedAt: string;
      tasks: Array<{ taskId: string; taskNumber: number; title: string; count: number }>;
    }>;
  };
}

interface PaginatedResponse {
  data: SectionReviewTrigger[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * A single Task (DB: sopTasks) inside one Job, as returned by
 * GET /assessments/sops/:sopId/sections/:sectionId/tasks/lookup (Phase 36.1a).
 * Declared here rather than in @guild/shared because that endpoint is the only
 * producer and this file's hook the only consumer.
 */
interface SectionTaskLookupItem {
  id: string;
  taskNumber: number;
  title: string;
  stepCount: number;
}

export const sectionReviewKeys = {
  all: ['section-review'] as const,
  lists: () => [...sectionReviewKeys.all, 'list'] as const,
  list: (page: number) => [...sectionReviewKeys.lists(), page] as const,
  count: () => [...sectionReviewKeys.all, 'count'] as const,
  sectionTasksLookup: (sopId: string, sectionId: string) =>
    [...sectionReviewKeys.all, 'section-tasks-lookup', sopId, sectionId] as const,
};

export function useSectionReviews(page: number = 1) {
  const api = useApi();
  return useQuery({
    queryKey: sectionReviewKeys.list(page),
    queryFn: () =>
      api.get<PaginatedResponse>(
        `/api/assessments/triggers/pending-section-review?page=${page}`
      ),
  });
}

export function useSectionReviewCount(roleLevel: number = 0) {
  const api = useApi();
  return useQuery({
    queryKey: sectionReviewKeys.count(),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse>(
        '/api/assessments/triggers/pending-section-review?page=1&limit=1'
      );
      return res.pagination?.total ?? 0;
    },
    enabled: roleLevel >= 4, // Only trainers (level 4+) can access this endpoint
    staleTime: 30000, // 30 seconds - badge can be slightly stale
    refetchInterval: 30_000, // Poll every 30s for badge count
  });
}

/**
 * Fetch the Tasks inside one Job, for Job Review's optional Task select
 * (Phase 36.1a).
 *
 * Lives here rather than in `useSOPs.ts` because the endpoint is mounted on the
 * assessments router (see the handler comment in `routes/assessments.ts` for
 * why) and its only consumer is this page — keeping the hook, the key factory
 * entry and the URL in one namespace.
 *
 * No staleTime, matching `useSopSectionsLookup()`: a SOP reprocess deletes and
 * recreates every task with fresh UUIDs, and a cached list would offer ids that
 * no longer resolve — which the assign handler answers with a 400.
 */
export function useSopSectionTasksLookup(sopId: string | null, sectionId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: sectionReviewKeys.sectionTasksLookup(sopId ?? '', sectionId ?? ''),
    queryFn: () =>
      api.get<SectionTaskLookupItem[]>(
        `/api/assessments/sops/${sopId}/sections/${sectionId}/tasks/lookup`
      ),
    enabled: !!sopId && !!sectionId,
  });
}

export function useAssignSection() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    // Phase 36.1a: `taskId` is optional and capture-only — omitted entirely
    // when the trainer leaves the Task select empty, which the API treats as
    // NULL. It never gates the mutation.
    mutationFn: ({
      triggerId,
      sectionId,
      taskId,
    }: {
      triggerId: string;
      sectionId: string;
      taskId?: string;
    }) =>
      api.post(`/api/assessments/triggers/${triggerId}/assign-section`, {
        sectionId,
        ...(taskId ? { taskId } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sectionReviewKeys.all });
    },
  });
}

export type { SectionReviewTrigger, SectionTaskLookupItem };
